// ─── «Textos clave» de una sesión: de texto a citas y de vuelta (S2) ────────
// La notación es la que ya imprime el PDF del syllabus: «Jn 1:1-3», «1 Co 13»,
// «Sal 23:1, 4». Varias citas se separan con PUNTO Y COMA, porque la coma ya
// separa versículos dentro de una cita.
//
// Los límites son los del esquema de importación de plantillas
// (course-template-import.schema.ts): libro hasta 40 caracteres, capítulo de 1 a
// 200, versículos hasta 40 caracteres, hasta 20 citas por sesión.

export interface ScriptureRef {
  book: string;
  chapter: number;
  verses?: string;
}

export const MAX_SCRIPTURE_REFS = 20;

export function formatScriptureRef(r: ScriptureRef): string {
  return `${r.book} ${r.chapter}${r.verses ? `:${r.verses}` : ''}`;
}

export function formatScriptureRefs(refs: readonly ScriptureRef[] | undefined): string {
  return (refs ?? []).map(formatScriptureRef).join('; ');
}

export type ParseScriptureRefs =
  | { ok: true; refs: ScriptureRef[] }
  | { ok: false; error: 'invalid'; part: string }
  | { ok: false; error: 'too_many'; max: number };

// Libro: un número opcional («1 Co», «2Ti») y una o más palabras con letras,
// tildes o punto. Capítulo: 1 a 3 dígitos. Versículos: «1», «1-3», «1-3, 5».
const REF =
  /^((?:[1-3]\s?)?\p{L}[\p{L}.]*(?:\s\p{L}[\p{L}.]*)*)\s+(\d{1,3})(?:\s*:\s*(\d+[a-c]?(?:\s*[-–]\s*\d+[a-c]?)?(?:\s*,\s*\d+[a-c]?(?:\s*[-–]\s*\d+[a-c]?)?)*))?$/u;

/** Texto vacío = sin textos clave (`refs: []`). */
export function parseScriptureRefs(text: string): ParseScriptureRefs {
  const parts = text
    .split(';')
    .map((p) => p.trim().replace(/\s+/g, ' '))
    .filter((p) => p.length > 0);
  if (parts.length > MAX_SCRIPTURE_REFS) {
    return { ok: false, error: 'too_many', max: MAX_SCRIPTURE_REFS };
  }
  const refs: ScriptureRef[] = [];
  for (const part of parts) {
    const m = REF.exec(part);
    const chapter = m ? Number(m[2]) : 0;
    const book = m?.[1] ?? '';
    const verses = m?.[3]?.replace(/\s+/g, '').replace(/,/g, ', ').replace(/–/g, '-');
    if (!m || chapter < 1 || chapter > 200 || book.length > 40 || (verses?.length ?? 0) > 40) {
      return { ok: false, error: 'invalid', part };
    }
    refs.push({ book, chapter, ...(verses ? { verses } : {}) });
  }
  return { ok: true, refs };
}
